import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { CreateTenantResponse, InviteSendResult } from '@/api/types'
import { Badge, Button, ButtonLink, Card, CopyButton, ExternalLinkIcon, InlineAlert, KeyValueList, type BadgeTone } from '@/components/ui'
import { CheckIcon } from '@/components/ui/icons'
import { formatDate, titleCase } from '@/lib/format'
import { tenantLinks } from '@/lib/tenant'

const DNS_TONE: Record<CreateTenantResponse['provisioning']['subdomain_dns'], BadgeTone> = { ok: 'good', skipped: 'neutral', failed: 'bad' }
const DNS_LABEL: Record<CreateTenantResponse['provisioning']['subdomain_dns'], string> = { ok: 'DNS ready', skipped: 'DNS skipped', failed: 'DNS failed' }

/** The first-admin invite sent after the create: its result, or why it failed. */
export interface InviteOutcome {
  email: string
  result?: InviteSendResult
  error?: string
}

/** After a successful create: where the tenant lives and what happened. */
export function DoneScreen({ result, invite, onAnother }: { result: CreateTenantResponse; invite: InviteOutcome | null; onAnother: () => void }) {
  const { tenant, provisioning } = result
  const links = tenantLinks(tenant)
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => heading.current?.focus(), [])

  return (
    <Card className="max-w-2xl">
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-good-soft text-good">
          <CheckIcon size={18} />
        </span>
        <div className="min-w-0">
          <h2 ref={heading} tabIndex={-1} className="break-words text-2xl font-extrabold leading-none text-ink focus:outline-none">
            {tenant.name} is ready
          </h2>
          <p className="mt-1.5 text-[13.5px] text-muted">The league, its {titleCase(tenant.default_sport_id)} pack and the trial were created together.</p>
        </div>
      </div>

      <KeyValueList
        className="mt-5"
        items={[
          {
            label: 'Public site',
            value: links.subdomainUrl ? (
              <a href={links.subdomainUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all font-mono text-xs text-accent hover:underline">
                {links.subdomainUrl.replace(/^https:\/\//, '')}
                <ExternalLinkIcon size={12} className="shrink-0" />
              </a>
            ) : (
              '—'
            ),
          },
          {
            label: 'Trial',
            value: tenant.account.trial_ends_at ? `Ends ${formatDate(tenant.account.trial_ends_at)}` : '—',
          },
          {
            label: 'First admin',
            value: <FirstAdmin invite={invite} tenantId={tenant.id} />,
          },
          {
            label: 'DNS',
            value: (
              <span className="grid justify-items-start gap-1">
                <Badge tone={DNS_TONE[provisioning.subdomain_dns]}>{DNS_LABEL[provisioning.subdomain_dns]}</Badge>
                {provisioning.message ? <span className="text-xs text-muted">{provisioning.message}</span> : null}
              </span>
            ),
          },
        ]}
      />

      {provisioning.subdomain_dns === 'failed' ? (
        <InlineAlert tone="warn" className="mt-4" title="The tenant exists, but its DNS didn't provision">
          Retry from the tenant's Domains tab. Nothing was rolled back.
        </InlineAlert>
      ) : null}

      <div className="mt-6 flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:justify-end">
        <Button onClick={onAnother}>Create another</Button>
        <ButtonLink to={`/tenants/${tenant.id}/overview`} variant="primary">
          Open {tenant.name}
        </ButtonLink>
      </div>
    </Card>
  )
}

function FirstAdmin({ invite, tenantId }: { invite: InviteOutcome | null; tenantId: number }) {
  const people = `/tenants/${tenantId}/people`
  if (!invite) return <span className="text-warn">None yet: invite one on the People tab</span>
  if (invite.error || !invite.result) {
    return (
      <span className="grid gap-1">
        <span className="break-words text-bad">Couldn&apos;t send the invite: {invite.error ?? 'unknown error'}</span>
        <Link to={people} className="text-xs font-semibold text-accent hover:underline">
          Invite from the People tab
        </Link>
      </span>
    )
  }
  const r = invite.result
  if (r.delivery === 'sent') return <span className="break-all">Invite sent to {invite.email}</span>
  if (!r.link) {
    return (
      <span className="grid gap-1">
        <span className="break-all">Invite created for {invite.email}</span>
        <span className="text-xs text-bad">This league has no ScoreLM address yet. Set its subdomain on the Domains tab.</span>
      </span>
    )
  }
  return (
    <span className="grid justify-items-start gap-1">
      <span className="break-all">Invite created for {invite.email}</span>
      <span className="text-xs text-warn">
        {r.delivery === 'not_configured'
          ? "Email isn't set up on this server: copy the link and send it yourself."
          : r.delivery === 'undeliverable'
            ? "This address can't receive email: copy the link and send it yourself."
            : "The email didn't go out. Copy the link and send it yourself."}
      </span>
      <CopyButton value={r.link} label="Copy link" ariaLabel={`Copy invite link for ${invite.email}`} />
    </span>
  )
}
